import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import {
  MagnifyingGlass, ShieldCheck, Camera, Ruler, Tag, Storefront,
  ShareNetwork, Package, Trophy, ArrowRight, BookOpen,
} from "@phosphor-icons/react";

const STAGES = [
  {
    icon: MagnifyingGlass,
    title: "Source like a pro",
    tips: [
      "Snap first, decide second — ID the era, maker & style before you spend a dime.",
      "Hunt for reproduction red-flags: modern screws, printed “woodgrain,” staples, too-perfect wear.",
      "Look underneath and behind — maker's marks, labels & joinery tell the real story.",
      "Negotiate on facts: know the sold comps before you counter.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Authenticate & attribute",
    tips: [
      "Match marks and labels to known references — a confirmed name multiplies value.",
      "Dovetails, patina and construction date a piece more honestly than a label alone.",
      "Unsure? Say “attributed to” — never overclaim. Trust is your brand.",
      "Keep proof: photograph marks, receipts & provenance for the listing and any dispute.",
    ],
  },
  {
    icon: Camera,
    title: "Photograph to sell",
    tips: [
      "Shoot in soft, even daylight — no harsh flash, no yellow lamplight.",
      "Lead with a clean, straight-on hero; then angles, details, marks & every flaw.",
      "Use the White-Background tool for curated platforms; add one styled shot for scale.",
      "5–8 sharp images beat 20 blurry ones — these are your best set.",
    ],
  },
  {
    icon: Ruler,
    title: "Measure & describe",
    tips: [
      "Measure W × D × H (and seat height for chairs) — accuracy prevents costly returns.",
      "Be honest about condition; name the wear before a buyer does.",
      "Bring an accurate description and let AI fill the rest — you add price & photos.",
      "Write for the buyer's search: maker, era, material, style, dimensions.",
    ],
  },
  {
    icon: Tag,
    title: "Price for profit",
    tips: [
      "Study sold comps, not asking prices — what actually cleared, and how fast.",
      "Bake the platform's cut into your list price so your take-home is what you want.",
      "Pick a lane: price to move, or price for top dollar and be patient.",
      "Leave ~10–15% negotiating room where haggling is expected.",
    ],
  },
  {
    icon: Storefront,
    title: "Choose the right marketplace",
    tips: [
      "Send your most valuable pieces where top dollar lives — Chairish & 1stDibs.",
      "Match piece to audience: prestige & rarity → 1stDibs; broad quality vintage → Chairish.",
      "Read the fine print: commission, monthly plan, returns & payout timing.",
      "Don't bet one piece on one audience — keep your record portable.",
    ],
  },
  {
    icon: ShareNetwork,
    title: "List once, cross-list everywhere",
    tips: [
      "Build one master record you own — then translate it to each marketplace.",
      "Auto-Fill from a Chairish or 1stDibs link, or straight from your own description.",
      "Approve every AI-written line — it should sound like you.",
      "Reroute rejects instantly to a better-fit marketplace instead of starting over.",
    ],
  },
  {
    icon: Package,
    title: "Ship & protect yourself",
    tips: [
      "Use white-glove / freight partners for anything large or fragile.",
      "Photograph the piece, dated, right before it leaves your hands.",
      "Pack for the worst day in transit — corners, glass & legs first.",
      "Know the return window and who pays — you already priced it in.",
    ],
  },
  {
    icon: Trophy,
    title: "After the sale",
    tips: [
      "Mark it sold and log your numbers — your Flip Scoreboard is your P&L.",
      "Reinvest your best margins into more of what actually sells.",
      "Keep the listing — reuse the knowledge next time instead of rebuilding it.",
      "Share the win — social proof brings the next buyer.",
    ],
  },
];

export default function ProSellerTips() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="pro-tips-page">
      <PublicHeader />

      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <div className="inline-flex items-center gap-2 border border-[#0D1B2A] px-3 py-1.5 mb-6">
          <BookOpen size={16} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">The need-to-know playbook</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.0] font-medium mb-5">
          Pro Seller Tips, <span className="italic text-[#B47A3C]">stage by stage.</span>
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl">
          Every step from the hunt to the sale — and the best practice at each. Skim it before a buying
          trip, or keep it open while you list. No fluff, just what moves quality pieces for top dollar.
        </p>
      </section>

      {/* STAGES */}
      <section className="max-w-4xl mx-auto px-6 pb-16 space-y-5">
        {STAGES.map((s, i) => (
          <div
            key={s.title}
            data-testid={`tip-stage-${i}`}
            className="vv-fade-up border-2 border-[#0D1B2A] bg-white p-6 sm:p-8 grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-8 items-start"
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <div className="flex sm:flex-col items-center sm:items-start gap-3">
              <span className="font-serif-display text-5xl font-bold text-[#E7E3DA] leading-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="h-11 w-11 bg-[#0D1B2A] flex items-center justify-center shrink-0">
                <s.icon size={22} weight="fill" className="text-[#FDFBF7]" />
              </span>
            </div>
            <div>
              <h2 className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">{s.title}</h2>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {s.tips.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[15px] leading-snug text-[#33302B]">
                    <span className="mt-2 h-1.5 w-1.5 bg-[#B47A3C] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="border-2 border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight mb-2">
              Put the playbook to work.
            </h3>
            <p className="text-[#D8D3C8] text-[17px] max-w-xl">
              Bring a spreadsheet, a Chairish or 1stDibs link, or just your photos — and turn hours of
              work into minutes.
            </p>
          </div>
          <button
            onClick={() => navigate(user ? "/dashboard" : "/register")}
            data-testid="pro-tips-cta"
            className="shrink-0 h-14 px-8 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-95"
          >
            {user ? "Go to dashboard" : "Start free"} <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>
    </div>
  );
}
